import axios from "axios";
import { loginSuccess } from "@/store/feature/auth/authSlice";
import { getServiceConfig } from "@/config/serviceConfig";

// Store injection to avoid circular dependency
let store: any = null;
export const injectStoreForTokenRefresh = (_store: any) => {
  store = _store;
};

interface RefreshTokenResponse {
  data: {
    access_token: string;
    refresh_token?: string; // Optional - server may or may not return new refresh token
  };
}

/**
 * Attempts to refresh the access token using the refresh token
 * @returns {Promise<{access_token: string, refresh_token?: string} | null>}
 */
export async function refreshAccessToken(): Promise<{
  access_token: string;
  refresh_token?: string;
} | null> {
  if (typeof window === "undefined") return null;

  const refreshToken = localStorage.getItem("refresh_token");
  if (!refreshToken) {
    console.warn("No refresh token available");
    return null;
  }

  try {
    const config = await getServiceConfig();
    // Default to the config's authService, or fallback if needed
    const authUrl = config.authService;

    if (!authUrl) {
      console.warn("Auth service URL not found in config");
      return null;
    }

    // Use only /refresh-token endpoint
    const response = await axios.post<RefreshTokenResponse>(
      `${authUrl}/refresh-token`,
      {
        refresh_token: refreshToken,
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
        // Don't use the axios instance to avoid interceptors
        validateStatus: () => true, // Don't throw on any status
      }
    );

    if (response.status === 200 && response.data?.data?.access_token) {
      const { access_token, refresh_token } = response.data.data;

      // Update localStorage
      localStorage.setItem("access_token", access_token);
      if (refresh_token) {
        localStorage.setItem("refresh_token", refresh_token);
      }

      // Update Redux store
      const userData = localStorage.getItem("user");
      if (userData) {
        try {
          const user = JSON.parse(userData);
          if (store) {
            store.dispatch(
              loginSuccess({
                user,
                access_token,
                refresh_token: refresh_token || refreshToken,
              })
            );
          } else {
            console.warn("TokenRefresh: Store not injected, skipping loginSuccess dispatch");
          }
        } catch (e) {
          console.error("Failed to parse user data:", e);
        }
      }

      // Update cookies
      if (typeof document !== "undefined") {
        document.cookie = `access_token=${access_token}; path=/;`;
        if (refresh_token) {
          document.cookie = `refresh_token=${refresh_token}; path=/;`;
        }
      }

      // Update last activity
      localStorage.setItem("lastActivity", Date.now().toString());

      return {
        access_token,
        refresh_token: refresh_token || refreshToken,
      };
    }

    // Refresh failed
    console.error("Token refresh failed:", response.status, response.data);
    return null;
  } catch (error: any) {
    console.error("Token refresh error:", error);
    return null;
  }
}

/**
 * Checks if the access token is expired or will expire soon
 * @param token - JWT access token
 * @param bufferMinutes - Buffer time in minutes before expiry (default: 5)
 * @returns {boolean}
 */
export function isTokenExpiringSoon(
  token: string | null,
  bufferMinutes: number = 5
): boolean {
  if (!token) return true;

  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    if (!payload.exp) return true;

    const expiryTime = payload.exp * 1000; // Convert to milliseconds
    const now = Date.now();
    const bufferTime = bufferMinutes * 60 * 1000; // Convert minutes to milliseconds

    return now >= expiryTime - bufferTime;
  } catch (error) {
    console.error("Failed to parse token:", error);
    return true;
  }
}

/**
 * Gets the token expiry time in milliseconds
 * @param token - JWT access token
 * @returns {number | null}
 */
export function getTokenExpiryTime(token: string | null): number | null {
  if (!token) return null;

  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    if (!payload.exp) return null;
    return payload.exp * 1000; // Convert to milliseconds
  } catch (error) {
    console.error("Failed to parse token:", error);
    return null;
  }
}

/**
 * Gets the token expiration duration in minutes from the current time
 * @param token - JWT access token
 * @returns {number | null} - Duration in minutes, or null if token is invalid/expired
 */
export function getTokenExpirationDurationMinutes(token: string | null): number | null {
  if (!token) return null;

  try {
    const expiryTime = getTokenExpiryTime(token);
    if (!expiryTime) return null;

    const now = Date.now();
    const remainingMs = expiryTime - now;

    // If token is already expired, return 0
    if (remainingMs <= 0) return 0;

    // Convert milliseconds to minutes
    const durationMinutes = Math.floor(remainingMs / (60 * 1000));
    return durationMinutes;
  } catch (error) {
    console.error("Failed to get token expiration duration:", error);
    return null;
  }
}

